import {
  Box,
  Container,
  Typography,
  useTheme,
  Fade,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useExpensesInRange } from "../../hooks/expense";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  LoginOutlined, 
  TrendingDown,
} from "@mui/icons-material";
import { getMonthsLongNames } from "../../utils/DateUtil";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import ExpenseTableComponent from "../../components/finance/reports/ExpensesTableComponent";
import { PageContainer, ModernCard, GradientButton } from "../../components/common/StyledComponents";
import MonthYearPicker from "../../components/common/MonthYearPicker";
import { getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../utils/DateUtil";

const ExpensePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [, setExpenseCategories] = useState<{ [category: string]: number }>({});
  const [, setBudgetUpdateTrigger] = useState(0);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions

  const updateSearchParams = useCallback((newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  }, [searchParams, setSearchParams]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  
  const { data: expenses = [] } = useExpensesInRange(startDate, endDate);
  
  // Fetch expense categories for budget overview
  const fetchExpenseCategories = useCallback(() => {
    if (!isAuthenticated) return;
    
    // Group expenses by category
    const categoriesMap: { [category: string]: number } = {};
    expenses.forEach(expense => {
      categoriesMap[expense.category] = (categoriesMap[expense.category] || 0) + expense.amount;
    });
    
    setExpenseCategories(categoriesMap);
  }, [expenses, isAuthenticated]);

  const handleBudgetUpdated = () => {
    setBudgetUpdateTrigger(prev => prev + 1);
  };

  useEffect(() => {
    fetchExpenseCategories();
  }, [fetchExpenseCategories]);

  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        });
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <TrendingDown sx={{ fontSize: '4rem', color: theme.palette.error.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Track Your Expenses
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Get detailed insights into your spending patterns. Categorize expenses, set budgets, and monitor your financial health with comprehensive reports and analytics.
            </Typography>
            <GradientButton
              variant="contained"
              startIcon={<LoginOutlined />}
              onClick={() => navigate(LOGIN_ROUTE_PATH)}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                px: 4,
                py: 1.5,
              }}
            >
              Sign In to Continue
            </GradientButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={true} timeout={400}>
          <Box>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <TrendingDown sx={{ fontSize: '2.5rem', color: theme.palette.error.main }} />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Expense Tracker
                </Typography>
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Track and categorize your spending for better financial control
              </Typography>
            </Box>

            {/* Period Selector */}
            <Box sx={{ mb: 3 }}>
              <ModernCard sx={{ p: 2.5 }}>
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthChange={handleMonthChange}
                  onYearChange={handleYearChange}
                  colorTheme="error"
                  transactionCount={expenses.length}
                  isLoading={false}
                />
              </ModernCard>
            </Box>

            {/* Budget Overview */}
            {/* {isAuthenticated && (
              <BudgetOverviewCard
                year={selectedYear}
                month={selectedMonth}
                isExpense={true}
                categories={expenseCategories}
                onBudgetUpdated={handleBudgetUpdated}
              />
            )} */}

            {/* Table Section with Animation */}
            <Fade in={true} timeout={600} key={dataKey}>
              <ModernCard>
                <ExpenseTableComponent 
                  year={selectedYear} 
                  month={selectedMonth}
                  onExpenseChange={handleBudgetUpdated}
                />
              </ModernCard>
            </Fade>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default ExpensePage;
